import type { Schemas } from '@argzombie/api-client';
import {
  conditionCureDefaults,
  conditionTypeDefaults,
  conditionsDefaults,
  withDefaults,
} from '../teamsDefaults';
import { type Bundle, type ConditionsDef, CONDITION_IDS } from './types';
import { isBlank, num, type Err } from './common';
import { STAT_IDS, STAT_MAX } from '../hb/types';

/**
 * The `conditions` block (ADR 0031): a port of packages/shared-cs/Runtime/Content/ContentValidator.Conditions.cs with the same
 * messages. Absent = the C# defaults (off, no types); an explicit `null` is "Bundle is missing 'conditions'.".
 */
export function validateConditions(
  b: Bundle,
  items: Set<string>,
  modules: Set<string>,
  modulesList: (Schemas['BaseModuleDef'] | null | undefined)[],
  err: Err,
) {
  if (b.conditions === null) {
    err('conditions', undefined, '', "Bundle is missing 'conditions'.");
    return;
  }
  const c = withDefaults(conditionsDefaults(), b.conditions ?? conditionsDefaults());
  const fail = (path: string, message: string) => err('conditions', undefined, path, message);
  const share = (field: keyof ConditionsDef & string) => {
    const v = num(c[field] as number | undefined);
    if (!(v >= 0 && v <= 1)) fail(field, `Conditions need ${field} in 0..1.`);
  };
  share('infirmaryCutPerLevel');
  share('woundedBelowHealthFrac');
  share('laidUpBelowHealthFrac');
  share('shortageSickChance');
  share('riskySickChance');
  share('biteChancePer10Damage');
  share('maxBiteChance');
  const unitOpen = (field: keyof ConditionsDef & string) => {
    const v = num(c[field] as number | undefined);
    if (!(v > 0) || v > 1) fail(field, `Conditions need ${field} in (0..1].`);
  };
  unitOpen('minDurationMult');
  unitOpen('minMaxHealthMult');
  unitOpen('playerTurnedMaxHealthMult');
  if (num(c.laidUpBelowHealthFrac) > num(c.woundedBelowHealthFrac))
    fail(
      'laidUpBelowHealthFrac',
      'Conditions need laidUpBelowHealthFrac <= woundedBelowHealthFrac.',
    );
  if (num(c.starvingDaysToLayUp) < 0)
    fail('starvingDaysToLayUp', 'Conditions starvingDaysToLayUp is negative.');

  if (c.types === null) {
    fail('types', "Bundle is missing 'conditions.types'.");
    return;
  }
  const seen = new Set<string>();
  (c.types ?? []).forEach((raw, j) => {
    if (raw == null) {
      fail(`types.${j}`, "Bundle has an empty entry in 'conditions.types'.");
      return;
    }
    const t = withDefaults(conditionTypeDefaults(), raw);
    const id = t.id ?? '';
    const at = (field: string) => `types.${j}.${field}`;
    if (!(CONDITION_IDS as readonly string[]).includes(id)) {
      fail(at('id'), `Condition '${id}' is unknown (wounded, laid_up, sick or bitten).`);
      return;
    }
    if (seen.has(id)) fail(at('id'), `Condition '${id}' is listed twice.`);
    seen.add(id);
    const w = `Condition '${id}'`;
    if (isBlank(t.name)) fail(at('name'), `${w} needs a name.`);
    for (const k of ['homeHours', 'awayHours', 'turnHours'] as const)
      if (num(t[k]) < 0) fail(at(k), `${w} ${k} is negative.`);
    for (const k of ['maxHealthMult', 'sprintMult'] as const) {
      const v = num(t[k]);
      if (!(v > 0) || v > 1) fail(at(k), `${w} needs ${k} in (0..1].`);
    }
    if (num(t.moveNoiseMult) < 1 || num(t.moveNoiseMult) > 3)
      fail(at('moveNoiseMult'), `${w} needs moveNoiseMult in 1..3.`);
    if (num(t.regenMult) < 0 || num(t.regenMult) > 1)
      fail(at('regenMult'), `${w} needs regenMult in 0..1.`);
    const cures = t.cures ?? [];
    if (num(t.homeHours) <= 0 && num(t.awayHours) <= 0 && cures.length === 0)
      fail(at('cures'), `${w} never ends: give it recovery hours or a cure.`);
    // ContentValidator.Effects.cs StatMods (ADR 0045 §7: the condition adapter reads these; PLAN §10).
    for (const [k, v] of Object.entries(t.statMods ?? {})) {
      if (!(STAT_IDS as readonly string[]).includes(k))
        fail(at('statMods'), `${w} statMods uses unknown stat '${k}'.`);
      else if (num(v) < -STAT_MAX || num(v) > STAT_MAX)
        fail(at('statMods'), `${w} statMods.${k} needs a value in -${STAT_MAX}..${STAT_MAX}.`);
    }
    cures.forEach((rawCure, k) => {
      const base = at(`cures.${k}`);
      const cure = rawCure == null ? null : withDefaults(conditionCureDefaults(), rawCure);
      if (cure == null || !items.has(cure.itemId ?? ''))
        fail(`${base}.itemId`, `${w} has a cure with unknown item '${cure?.itemId ?? ''}'.`);
      if (cure == null || !cure.moduleId) return;
      if (!modules.has(cure.moduleId)) {
        fail(`${base}.moduleId`, `${w} cure needs unknown module '${cure.moduleId}'.`);
        return;
      }
      const levels = modulesList.find((m) => m?.id === cure.moduleId)?.levels?.length ?? 0;
      const level = num(cure.moduleLevel);
      if (level < 1 || level > levels)
        fail(
          `${base}.moduleLevel`,
          `${w} cure needs level ${level} of module '${cure.moduleId}', which has levels 1..${levels}.`,
        );
    });
  });
}
